/**
 * useRealtimeEvents — SSE hook for real-time cache invalidation
 *
 * Connects to GET /v1/events?token=<jwt> and invalidates React Query
 * caches when the server pushes an invalidation event.
 *
 * Reconnects automatically with exponential backoff on disconnect.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { config } from '@/config';
import { getToken } from '@/services/api';

const INITIAL_RECONNECT_MS = 3_000;
const MAX_RECONNECT_MS = 60_000;

export function useRealtimeEvents(): void {
    const queryClient = useQueryClient();
    const esRef = useRef<EventSource | null>(null);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reconnectDelayRef = useRef(INITIAL_RECONNECT_MS);
    const unmountedRef = useRef(false);

    useEffect(() => {
        unmountedRef.current = false;

        function connect() {
            const token = getToken();
            if (!token || unmountedRef.current) return;

            const url = `${config.apiUrl}/v1/events?token=${encodeURIComponent(token)}`;
            const es = new EventSource(url);
            esRef.current = es;

            // Initial handshake — reset backoff
            es.addEventListener('connected', () => {
                reconnectDelayRef.current = INITIAL_RECONNECT_MS;
            });

            // Invalidation events from backend mutations
            es.onmessage = (e: MessageEvent<string>) => {
                try {
                    const { entities } = JSON.parse(e.data) as { entities?: string[] };
                    if (entities?.length) {
                        for (const entity of entities) {
                            queryClient.invalidateQueries({ queryKey: [entity] });
                        }
                    }
                } catch {
                    // Ignore malformed events
                }
            };

            // On error: close, wait with backoff, retry
            es.onerror = () => {
                es.close();
                esRef.current = null;
                if (unmountedRef.current) return;

                reconnectTimerRef.current = setTimeout(() => {
                    if (!unmountedRef.current) connect();
                }, reconnectDelayRef.current);

                // Exponential backoff, capped at MAX_RECONNECT_MS
                reconnectDelayRef.current = Math.min(
                    reconnectDelayRef.current * 2,
                    MAX_RECONNECT_MS,
                );
            };
        }

        connect();

        return () => {
            unmountedRef.current = true;
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            esRef.current?.close();
            esRef.current = null;
        };
    }, [queryClient]);
}
